import React, { useState, useEffect, useMemo, useRef, useCallback, useContext } from 'react'
import styles from './SpecialModeView.module.scss'
import { speak } from '../speech/speak'
import voices from '../speech/voices'
import languages from '../languages'
import { celebrate, popScale, shake } from './juice'
import { romanizationOf } from '../speech/flowMatch'
import HandwritingView from './HandwritingView'
import TurkishVerbView, { TR_TENSES } from './TurkishVerbView'
import GermanAdjectiveView, { DEU_PATTERNS } from './GermanAdjectiveView'
import VerbFlowView from './VerbFlowView'
import KoreanSoundsView from './KoreanSoundsView'
import TurkishSoundsView from './TurkishSoundsView'
import { HangulQuiz, HangulWordCloze } from './HangulPractice'
import { HANGUL } from '../data/drills/hangulLetters'
import useLetterStats from '../hooks/useLetterStats'
import { KOR_WORDS } from '../data/drills/korWords'
import { RU_CASE_SETS } from '../data/drills/rusCases'
import { buildLadder, rungsForLevel, capNoteForLevel, LEVELS as RU_LEVELS } from '../data/drills/ruModels'
import { imageToTiles } from '../util/imageTiles'
import { cueCorrect, cueWrong, cueComplete, cueTick, isMuted, setMuted } from './cues'
import UserContext from '../context/UserContext'
import useLearnerContext from '../hooks/useLearnerContext'

import { useQuery, useMutation } from '@apollo/client'
import { MY_TEACHERS_QUERY, SHARE_DRILL_RESULT_MUTATION } from '../query/gqlQueries'

import CloseIcon from '@mui/icons-material/Close'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import SkipNextIcon from '@mui/icons-material/SkipNext'
import TableChartIcon from '@mui/icons-material/TableChart'
import MusicNoteIcon from '@mui/icons-material/MusicNote'
import MusicOffIcon from '@mui/icons-material/MusicOff'

// Language-specific special modes. Only `ready` ones are runnable; the rest show
// as "coming soon" so the menu communicates the roadmap.
// The spoken verb drill (VerbFlowView, AI-built) used to live in Flashcards;
// it works for any language, so every language here offers it.
const VERB_FLOW = { id: 'verbflow', name: 'Verb Drill (spoken)', desc: 'Say the conjugated verb out loud — the AI builds the set', ready: true }
const SPECIAL_MODES = {
  rus: [{ id: 'cases', name: 'Noun Cases', desc: 'Pick the case, then form the ending', ready: true }, VERB_FLOW],
  kor: [
    { id: 'alphabet', name: 'Alphabet (한글)', desc: 'Tap a letter to hear its sound', ready: true },
    { id: 'sounds', name: 'Sound it out', desc: 'Watch each letter light up as you hear a word — then say it', ready: true },
    { id: 'draw', name: 'Character drawing', desc: 'Hear it, then write the 한글', ready: true },
    VERB_FLOW,
  ],
  tur: [
    { id: 'verbs', name: 'Verb Endings', desc: 'Pick the personal ending, then conjugate', ready: true },
    { id: 'sounds', name: 'Sound it out', desc: 'Syllables, stress and long vowels light up as you hear a word — then say it', ready: true },
    VERB_FLOW,
  ],
  deu: [{ id: 'adjectives', name: 'Adjective Endings', desc: 'Weak / mixed / strong, then the ending', ready: true }, VERB_FLOW],
  por: [VERB_FLOW],
  spa: [VERB_FLOW],
  ita: [VERB_FLOW],
  fra: [VERB_FLOW],
  jpn: [{ id: 'draw', name: 'Kana / Kanji drawing', desc: 'Trace characters by hand', ready: false }, VERB_FLOW],
  cmn: [{ id: 'draw', name: 'Hanzi drawing', desc: 'Trace characters by hand', ready: false }, VERB_FLOW],
}

// The Hangul alphabet table lives in data/drills/hangulLetters.js (shared with
// the board's practice modes in HangulPractice.js).

const CASES = [
  {
    key: 'nominative', label: 'Nominative', ru: 'Именительный', q: 'Кто? Что?', qEn: 'Who? What?',
    lines: ['The doer — the person or thing performing the action (the subject).'], ex: [],
  },
  {
    key: 'accusative', label: 'Accusative', ru: 'Винительный', q: 'Кого? Что?', qEn: 'Whom? What?',
    lines: ['The direct object — the person/thing that receives the action.', 'Also the destination when you go somewhere.'], ex: [],
  },
  {
    key: 'genitive', label: 'Genitive', ru: 'Родительный', q: 'Кого? Чего?', qEn: 'Of whom? Of what?',
    lines: ['Possession / "of" something.', 'Absence — after «нет».', 'Quantities.'],
    ex: [{ ru: 'машина моего друга', en: "my friend's car" }, { ru: 'У меня нет машины', en: "I don't have a car" }],
  },
  {
    key: 'prepositional', label: 'Prepositional', ru: 'Предложный', q: 'О ком? О чём?', qEn: 'About whom/what? · Where?',
    lines: ['Always used WITH a preposition.', 'Location (where?).', 'Talking / thinking about something.'],
    ex: [{ ru: 'Я живу в Москве', en: 'I live in Moscow' }, { ru: 'Я говорю о работе', en: 'I am talking about work' }],
  },
  {
    key: 'dative', label: 'Dative', ru: 'Дательный', q: 'Кому? Чему?', qEn: 'To whom? To what?',
    lines: ['Something given, said, sent or done TO someone.', 'Use with «по».'], ex: [],
  },
  {
    key: 'instrumental', label: 'Instrumental', ru: 'Творительный', q: 'Кем? Чем?', qEn: 'With whom? With what?',
    lines: ['Mainly with «с» = with.', 'Also profession / being something.'],
    ex: [{ ru: 'Он работает врачом', en: 'He works as a doctor' }],
  },
]
const CASE_BY_KEY = Object.fromEntries(CASES.map(c => [c.key, c]))

// Condensed declension table (adjective + noun endings + examples) for the
// guided reference board.
const DECL = {
  nominative: [
    { g: ['m'], adj: '-ый (-ий), -ой', noun: '— (zero)', ex: 'новый хороший студент, большой словарь' },
    { g: ['n'], adj: '-ое (-ее)', noun: '-о (-е, -ё)', ex: 'первое слово, синее платье' },
    { g: ['f'], adj: '-ая (-яя)', noun: '-а (-я)', ex: 'новая студентка, синяя тетрадь, Россия' },
    { num: 'pl', adj: '-ые (-ие)', noun: '-ы (-и) · n. -а (-я)', ex: 'новые студенты, словари, синие тетради' },
  ],
  accusative: [
    { g: ['m'], adj: '= Nom / = Gen', noun: '= Nom / = Gen', ex: 'новый учебник · нового студента' },
    { g: ['n'], adj: '= Nom', noun: '= Nom', ex: 'первое слово, синее платье' },
    { g: ['f'], adj: '-ую (-юю)', noun: '-у (-ю)', ex: 'новую студентку, синюю тетрадь, Россию' },
    { num: 'pl', adj: '= Nom / = Gen', noun: '= Nom / = Gen', ex: 'новые газеты · новых студентов' },
  ],
  genitive: [
    { g: ['m', 'n'], adj: '-ого (-его)', noun: '-а (-я)', ex: 'нового студента, словаря; синего платья' },
    { g: ['f'], adj: '-ой (-ей)', noun: '-ы (-и)', ex: 'новой студентки, синей тетради, России' },
    { num: 'pl', adj: '-ых (-их)', noun: '-ов (-ев, -ей) · -Ø, *-ей', ex: 'новых студентов, словарей; тетрадей' },
  ],
  prepositional: [
    { g: ['m', 'n'], adj: '-ом (-ем)', noun: '-е, *-и', ex: 'о новом студенте, словаре; о синем платье' },
    { g: ['f'], adj: '-ой (-ей)', noun: '-е, *-и', ex: 'о новой студентке, синей тетради, России' },
    { num: 'pl', adj: '-ых (-их)', noun: '-ах (-ях)', ex: 'о новых студентах, словарях, платьях' },
  ],
  dative: [
    { g: ['m', 'n'], adj: '-ому (-ему)', noun: '-у (-ю)', ex: 'новому студенту, словарю; синему платью' },
    { g: ['f'], adj: '-ой (-ей)', noun: '-е, *-и', ex: 'новой студентке, синей тетради, России' },
    { num: 'pl', adj: '-ым (-им)', noun: '-ам (-ям)', ex: 'новым студентам, словам, платьям' },
  ],
  instrumental: [
    { g: ['m', 'n'], adj: '-ым (-им)', noun: '-ом (-ем/ём)', ex: 'новым студентом, словарём; синим платьем' },
    { g: ['f'], adj: '-ой (-ей)', noun: '-ой (-ей), *-ю', ex: 'новой студенткой, Россией, тетрадью' },
    { num: 'pl', adj: '-ыми (-ими)', noun: '-ами (-ями)', ex: 'новыми студентами, словарями, платьями' },
  ],
}

// The DECL row that applies to one noun — shared by the reference board and the
// ending ladder, so both always describe the same cell.
const declRowFor = (caseKey, gender, number) => {
  const subs = DECL[caseKey] || []
  return number === 'pl' ? subs.find(s => s.num === 'pl') : (subs.find(s => s.g && s.g.includes(gender)) || subs[0])
}
// Bare endings out of a DECL noun cell: '-ов (-ев, -ей) · -Ø' → ov, ev, ej
const endingsOf = (row) => [...String(row?.noun || '').matchAll(/-([а-яё]+)/gi)].map(m => m[1].toLowerCase())
// The shortlist: every ending this gender/number takes across the six cases —
// exactly the set the learner has to choose between. Sorted, so position never
// hints at the answer; the true ending is unioned in so it is always present.
const shortlistFor = (gender, number, trueEnding) => {
  const all = Object.keys(DECL).flatMap(k => endingsOf(declRowFor(k, gender, number)))
  return Array.from(new Set([...(trueEnding ? [trueEnding] : []), ...all]))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, 'ru'))
}

const normGender = (g, noun) => {
  const s = String(g || '').toLowerCase()
  if (/^m|masc|муж/.test(s)) return 'm'
  if (/^f|fem|жен/.test(s)) return 'f'
  if (/^n|neut|сред/.test(s)) return 'n'
  const n = String(noun || '').trim().toLowerCase()
  if (/[ая]$/.test(n)) return 'f'
  if (/[оеё]$/.test(n)) return 'n'
  return 'm'
}

// Russian case selection is highly rule-based — the model was guessing (к →
// "accusative"). Handing it the triggers makes it far more reliable.
const RU_CASE_RULES = `Russian case triggers — apply STRICTLY (the case MUST match the trigger):
- к, по → DATIVE. Also the indirect recipient ("to/for someone") → DATIVE.
- о, об, при → PREPOSITIONAL. в/на meaning LOCATION ("where?") → PREPOSITIONAL.
- в, на, за, под, через (motion INTO/onto, "where to?") → ACCUSATIVE. A direct object of a transitive verb → ACCUSATIVE.
- из, от, до, для, без, у, около, после, против, вокруг, кроме → GENITIVE. "нет / не было" (there isn't) → GENITIVE. Possession / quantity / "of" → GENITIVE.
- с meaning "with" → INSTRUMENTAL. перед, над, под, за (location), между → INSTRUMENTAL. с meaning "from/off" → GENITIVE.
- The subject → NOMINATIVE.`

const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '').trim()

// Light / dark, sharing the key with StudentLessonView so one choice covers both.
const THEME_KEY = 'lessonTheme'
const readTheme = () => { try { const t = localStorage.getItem(THEME_KEY); return t === 'light' || t === 'dark' ? t : 'dark' } catch { return 'dark' } }

// Imported homework sets live on the device: the sheet changes every week, and a
// personal worksheet has no business on the shared server.
const IMPORTED_KEY = 'ruCaseImportedSets'
const readImported = () => { try { const v = JSON.parse(localStorage.getItem(IMPORTED_KEY) || '[]'); return Array.isArray(v) ? v : [] } catch { return [] } }
const saveImported = (sets) => { try { localStorage.setItem(IMPORTED_KEY, JSON.stringify(sets.slice(0, 20))) } catch { /* private mode */ } }

const parseItems = (raw) => {
  if (!raw) return []
  let t = String(raw).replace(/```json/gi, '').replace(/```/g, '').trim()
  const a = t.indexOf('{'), b = t.lastIndexOf('}')
  if (a !== -1 && b !== -1) t = t.slice(a, b + 1)
  try { const o = JSON.parse(t); return Array.isArray(o?.items) ? o.items : [] } catch { return [] }
}

const shapeCaseItem = (raw) => {
  const answer = String(raw?.answer || '').trim()
  const baseNoun = String(raw?.baseNoun || '').trim()
  const caseKey = String(raw?.case || '').trim().toLowerCase()
  if (!answer || !baseNoun || !CASE_BY_KEY[caseKey]) return null
  // Derive the visible stem + the case ending from the noun itself — the part
  // of the inflected form that differs from the dictionary form (учитель →
  // учител + ю). Always correct and always in Cyrillic; never the model's guessy
  // English transliteration ("-y").
  const A = baseNoun.toLowerCase(), B = answer.toLowerCase()
  let cp = 0
  while (cp < Math.min(A.length, B.length) && A[cp] === B[cp]) cp++
  let stem = answer.slice(0, cp)
  let ending = answer.slice(cp)
  if (cp === 0) { stem = ''; ending = answer }   // fully irregular — reveal whole word
  // Split the sentence around the target noun for display.
  const template = String(raw?.sentenceTemplate || '').trim()
  const sentence = String(raw?.sentence || '').trim()
  let prefix = '', suffix = ''
  const parts = template.split(/⟦[^⟧]*⟧|_{2,}|\{[^}]*\}/)
  if (template && parts.length >= 2) { prefix = parts[0]; suffix = parts.slice(1).join(' ') }
  else if (sentence) {
    const i = sentence.toLowerCase().indexOf(answer.toLowerCase())
    if (i >= 0) { prefix = sentence.slice(0, i); suffix = sentence.slice(i + answer.length) }
    else { prefix = sentence + ' '; suffix = '' }
  }
  return {
    baseNoun, caseKey, answer, stem, ending,
    gender: normGender(raw?.gender, baseNoun),
    number: /plural|pl|мн/i.test(String(raw?.number || '')) ? 'pl' : 'sg',
    prefix: prefix.trim(), suffix: suffix.trim(),
    sentence: sentence || `${prefix}${answer}${suffix}`,
    english: String(raw?.english || '').trim(),
    why: String(raw?.why || '').trim(),
    endingHint: String(raw?.endingHint || '').trim(),
  }
}

export default function SpecialModeView({ voiceSpeed, onClose }) {
  const { username } = useContext(UserContext)
  const { learning } = useLearnerContext(username)
  const [lang, setLang] = useState('rus')
  const [mode, setMode] = useState('cases')
  const [query, setQuery] = useState('')
  const [count, setCount] = useState(10)
  // Sound it out (Korean, Turkish): which syllable counts to draw words from,
  // plus any words the learner types ("비행기, 사과=apple" / "kitap, ev=house").
  const [soundLens, setSoundLens] = useState([1, 2])
  const [soundCustom, setSoundCustom] = useState('')
  const [drawLen, setDrawLen] = useState('1')     // '1' | '2' | '3' (up to N syllables)
  const [drawCards, setDrawCards] = useState([])
  const [trTense, setTrTense] = useState('present') // Turkish verb-endings: one tense at a time
  const [deuNumber, setDeuNumber] = useState('mixed')  // German adjectives: sg | pl | mixed
  const [deuPattern, setDeuPattern] = useState('all')  // all | weak | mixed | strong
  const [phase, setPhase] = useState('setup')   // setup | loading | drill | error
  const [errorMsg, setErrorMsg] = useState('')
  const [loadingMsg, setLoadingMsg] = useState('')
  const [regenerating, setRegenerating] = useState(false)

  const [items, setItems] = useState([])
  const [idx, setIdx] = useState(0)
  const [setNum, setSetNum] = useState(1)
  const [stage, setStage] = useState('case')     // case | ending | done
  const [caseChoice, setCaseChoice] = useState(null)
  const [caseResult, setCaseResult] = useState(null) // right | wrong | revealed
  const [caseAttempts, setCaseAttempts] = useState(0)
  const [typed, setTyped] = useState('')
  const [endAttempts, setEndAttempts] = useState(0)
  const [endRevealed, setEndRevealed] = useState(false)
  const [combo, setCombo] = useState(0)
  const [best, setBest] = useState(0)
  const [explainText, setExplainText] = useState(null)
  const [explainLoading, setExplainLoading] = useState(false)
  const [showExplain, setShowExplain] = useState(false)
  const [caseHintShort, setCaseHintShort] = useState(null)
  const [caseHintLong, setCaseHintLong] = useState(null)
  const [caseHintLoading, setCaseHintLoading] = useState(false)
  const [showCaseHint, setShowCaseHint] = useState(false)
  const [hintMode, setHintMode] = useState('short')
  const [showRef, setShowRef] = useState(false)  // guided endings reference board

  // Russian cases: where the exercises come from, and how the set went.
  const [ruSource, setRuSource] = useState('sets')          // sets | ai | import
  const [ruSetId, setRuSetId] = useState(RU_CASE_SETS[0]?.id || '')
  const [importedSets, setImportedSets] = useState(readImported)
  const [importFiles, setImportFiles] = useState([])
  const [setTitle, setSetTitle] = useState('')
  const [results, setResults] = useState({})                // idx → how that item went
  const [showSummary, setShowSummary] = useState(false)
  const [soundOn, setSoundOn] = useState(() => !isMuted())
  const [theme, setTheme] = useState(readTheme)
  const [ruLevel, setRuLevel] = useState('')                // '' = follow the profile
  const [endRung, setEndRung] = useState(0)                 // rungs of ending help opened

  const item = items[idx]
  const advanceTimerRef = useRef(null)
  const idxRef = useRef(0)
  const baseItemsRef = useRef([])   // the WHOLE set, so "redo everything" survives a tricky-only retry
  const inputRef = useRef(null)
  const cardRef = useRef(null)
  const comboRef = useRef(null)
  const caseRowRef = useRef(null)
  const completedRef = useRef(false)

  const serverUrl = process.env.REACT_APP_OLLAMA_URI
  const apiKey = process.env.REACT_APP_OLLAMA_KEY
  const langName = languages[lang] || lang
  const modesForLang = SPECIAL_MODES[lang] || []
  const activeMode = modesForLang.find(m => m.id === mode)

  useEffect(() => { if (combo > 0) popScale(comboRef.current) }, [combo])
  useEffect(() => { idxRef.current = idx }, [idx])
  const themeClass = theme === 'dark' ? styles.dark : styles.light
  const toggleTheme = () => setTheme(t => {
    const next = t === 'dark' ? 'light' : 'dark'
    try { localStorage.setItem(THEME_KEY, next) } catch { /* private mode */ }
    return next
  })
  const ThemeBtn = () => (
    <button className={styles.themeBtn} onClick={toggleTheme} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
      {theme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
    </button>
  )
  const allSets = useMemo(() => [...RU_CASE_SETS, ...importedSets], [importedSets])

  // How much ending help this learner gets: their profile level unless they
  // override it here. A beginner cannot "just remember" an ending they have
  // never met; a B2 who is handed it never has to retrieve it.
  const profileLevel = useMemo(() => (learning || []).find(l => /russian|русск/i.test(l?.name || ''))?.level || '', [learning])
  const effLevel = ruLevel || profileLevel || 'A2'

  // The nudges for this item — narrowing, never answering.
  const endLadder = useMemo(() => {
    if (!item) return []
    const c = CASE_BY_KEY[item.caseKey]
    const trueEnding = (item.ending || '').toLowerCase()
    return buildLadder({
      baseNoun: item.baseNoun, gender: item.gender, number: item.number,
      caseKey: item.caseKey, caseLabel: c?.label || '', caseQuestion: c?.q || '',
      trueEnding,
      chips: shortlistFor(item.gender, item.number, trueEnding),
      ruleText: declRowFor(item.caseKey, item.gender, item.number)?.noun || '',
    })
  }, [item])
  const maxRungs = Math.min(endLadder.length, rungsForLevel(effLevel))

  // How each item went, so the end-of-set card can show what to review.
  const recordResult = useCallback((i, patch) => {
    setResults(r => ({ ...r, [i]: { caseAttempts: 0, endAttempts: 0, revealed: false, hinted: false, ...(r[i] || {}), ...patch } }))
  }, [])
  // Default the mode to the first ready one when the language changes.
  useEffect(() => {
    const ready = (SPECIAL_MODES[lang] || []).find(m => m.ready)
    setMode(ready ? ready.id : ((SPECIAL_MODES[lang] || [])[0]?.id || ''))
  }, [lang])

  // ── generation ──────────────────────────────────────────────────────────
  const generate = useCallback(async (isRegen = false) => {
    if (!serverUrl) { setErrorMsg('Mac Studio (REACT_APP_OLLAMA_URI) is not configured.'); setPhase('error'); return }
    if (isRegen) setRegenerating(true); else setPhase('loading')
    setErrorMsg('')

    const prompt = `You are a Russian teacher creating a NOUN-CASE practice set (textbook fill-in-the-blank style).
${query.trim() ? `Focus: "${query.trim()}".` : 'Use a spread across all six cases (nominative, accusative, genitive, dative, instrumental, prepositional).'}

Create ${count} items. In each, exactly ONE noun must be put into the correct case; the rest of the sentence is already correct and natural. Prefer common, beginner-friendly nouns.

${RU_CASE_RULES}
CRITICAL: the "case" you assign MUST be the one the trigger in the sentence actually requires (check the preposition/role against the rules above), and "answer" MUST be the correctly inflected form. Do NOT leave the noun unchanged unless its form in that case genuinely equals the dictionary form. If unsure how a noun declines, pick an easier, very common noun you are certain about.

For each item provide:
- baseNoun: the target noun in its dictionary form (nominative singular)
- gender: the noun's gender — "m", "f" or "n"
- number: "singular" or "plural" (the form needed in the sentence)
- case: one of "nominative","accusative","genitive","dative","instrumental","prepositional"
- answer: the noun correctly inflected into that case (exactly the word that goes in the blank)
- stem: the unchanging leading part of "answer" (may be empty)
- ending: the rest, so that stem + ending EXACTLY equals answer
- sentenceTemplate: the full Russian sentence but with the target noun replaced by the token ⟦N⟧
- english: the English translation of the sentence
- why: ONE short English sentence explaining WHY this case is used (the trigger)
- endingHint: ONE short English hint for forming the ending — do NOT give the full answer

Return ONLY minified JSON, no markdown:
{"items":[{"baseNoun":"","gender":"","number":"","case":"","answer":"","stem":"","ending":"","sentenceTemplate":"","english":"","why":"","endingHint":""}]}`

    try {
      const res = await fetch(`${serverUrl}/api/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
        body: JSON.stringify({ model: 'gemma3:27b', prompt, stream: false, format: 'json', options: { temperature: 0.3, num_predict: 2600 } }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const built = parseItems(data.response).map(shapeCaseItem).filter(Boolean)
      if (built.length === 0) { setErrorMsg('The model returned no usable items — try again.'); setPhase('error'); setRegenerating(false); return }

      // ── grammar-check & correct pass ──────────────────────────────────────
      // Russian case + inflection is exactly where the model slips (к → dative,
      // not accusative). Re-verify every item against the rules and fix it
      // before the learner ever sees it. Best-effort: keep drafts if it fails.
      let final = built
      try {
        setRegenerating(true)
        const list = built.map((d, i) => `${i + 1}. "${d.prefix}⟦${d.baseNoun}⟧${d.suffix}" — draft says case=${d.caseKey}, answer=${d.answer}`).join('\n')
        const vres = await fetch(`${serverUrl}/api/generate`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
          body: JSON.stringify({
            model: 'gemma3:27b', stream: false, format: 'json', options: { temperature: 0.15, num_predict: 2400 },
            prompt: `You are a strict Russian grammar checker. Some drafts have the WRONG case or a wrong inflected form — correct every one.
${RU_CASE_RULES}
Items (⟦baseNoun⟧ is the dictionary form of the noun that must be inflected for the sentence):
${list}
Return ONLY JSON {"items":[{"case":"","answer":"","stem":"","ending":"","why":"","endingHint":""}]} with EXACTLY ${built.length} items in the SAME order. "case" = the case the sentence truly requires; "answer" = baseNoun correctly inflected into that case; stem + ending = answer; "why" = one short English sentence naming the trigger.`,
          }),
        })
        if (vres.ok) {
          const arr = parseItems((await vres.json()).response)
          if (arr.length === built.length) {
            const fixed = built.map((d, i) => shapeCaseItem({
              baseNoun: d.baseNoun, sentenceTemplate: `${d.prefix}⟦N⟧${d.suffix}`, english: d.english,
              gender: d.gender, number: d.number,
              case: arr[i]?.case || d.caseKey, answer: arr[i]?.answer || d.answer,
              stem: arr[i]?.stem || '', ending: arr[i]?.ending || '',
              why: arr[i]?.why || d.why, endingHint: arr[i]?.endingHint || d.endingHint,
            })).filter(Boolean)
            if (fixed.length) final = fixed
          }
        }
      } catch { /* keep drafts */ }

      baseItemsRef.current = final
      setItems(final); setIdx(0); setResults({}); setShowSummary(false); setSetTitle('')
      setSetNum(n => (isRegen ? n + 1 : 1)); setRegenerating(false); setPhase('drill')
    } catch (e) {
      setErrorMsg((e.message || '').match(/Failed to fetch|Load failed/) ? 'Connection to the Mac Studio failed — check the tunnel / Ollama.' : (e.message || 'Generation failed.'))
      setPhase('error'); setRegenerating(false)
    }
  }, [serverUrl, apiKey, query, count])

  // Korean handwriting: build a deck from the curated word list (correct
  // spellings), then hand it to the shared handwriting board with auto-speak on.
  const startDraw = () => {
    const buckets = drawLen === '1' ? [1] : drawLen === '2' ? [1, 2] : [1, 2, 3]
    const pool = buckets.flatMap(n => (KOR_WORDS[n] || []).map(([k, e]) => ({ k, e })))
    const cards = pool.sort(() => Math.random() - 0.5).slice(0, count).map(w => ({
      sourceId: w.k, source: w.k, sourceLanguage: 'kor',
      translations: [{ targetId: `${w.k}-e`, target: w.e, targetLanguage: 'eng' }],
    }))
    setDrawCards(cards)
    setPhase('drill')
  }
  // Run a fixed homework set — authored or previously imported. No model, no
  // network: the answers were verified once and stay verified.
  const startSet = (set) => {
    const built = (set?.items || []).map(shapeCaseItem).filter(Boolean)
    if (!built.length) { setErrorMsg('That set has no usable exercises.'); setPhase('error'); return }
    baseItemsRef.current = built
    setItems(built); setIdx(0); setResults({}); setShowSummary(false); setCombo(0)
    setSetTitle(set.title || 'Homework'); setSetNum(1); setPhase('drill')
  }

  // A screenshot of a worksheet → a drill, in two passes. The vision model only
  // READS (sentence + the bracketed dictionary form); a text model then works
  // out the case and the inflected form against the same trigger rules the
  // generator uses — reading and solving are what each model is good at.
  const importFromImages = async () => {
    if (!serverUrl) { setErrorMsg('Mac Studio (REACT_APP_OLLAMA_URI) is not configured.'); setPhase('error'); return }
    if (importFiles.length === 0) return
    setPhase('loading'); setErrorMsg(''); setLoadingMsg('Preparing the sheet…')

    const call = async (body) => {
      const res = await fetch(`${serverUrl}/api/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
        body: JSON.stringify({ stream: false, format: 'json', ...body }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return parseItems((await res.json()).response)
    }

    try {
      const ocrPrompt = `You are reading a Russian grammar worksheet. Every numbered exercise is a Russian sentence containing exactly ONE noun printed in parentheses in its dictionary form, followed by a blank the student must fill in.
For EACH exercise return:
- "sentence": the full Russian sentence, with BOTH the parenthesised noun and the blank replaced by the single token ⟦N⟧. Keep every other word and the final punctuation exactly as printed.
- "baseNoun": the word that was inside the parentheses, exactly as printed.
Ignore exercise numbers, "choose" dropdowns, empty answer boxes and page decoration. Do NOT translate, and do NOT fill in any blank.
Return ONLY minified JSON: {"items":[{"sentence":"","baseNoun":""}]}`

      const seen = new Set()
      const read = []
      let t = 0
      for (const file of importFiles) {
        const tiles = await imageToTiles(file)
        for (const b64 of tiles) {
          t++
          setLoadingMsg(`Reading the sheet… (page ${t})`)
          const got = await call({ model: 'qwen2.5vl:32b', prompt: ocrPrompt, images: [b64], options: { temperature: 0.1, num_predict: 1800 } })
          got.forEach(g => {
            const s = String(g?.sentence || '').trim()
            const b = String(g?.baseNoun || '').trim()
            const key = `${norm(s)}|${norm(b)}`
            if (!s || !b || !s.includes('⟦N⟧') || seen.has(key)) return
            seen.add(key); read.push({ sentence: s, baseNoun: b })
          })
        }
      }
      if (read.length === 0) throw new Error('No exercises could be read from that image. Try a sharper screenshot, or crop it to just the questions.')

      setLoadingMsg(`Working out the cases for ${read.length} exercises…`)
      const list = read.map((r, i) => `${i + 1}. "${r.sentence.replace('⟦N⟧', `⟦${r.baseNoun}⟧`)}"`).join('\n')
      const solved = await call({
        model: 'gemma3:27b', options: { temperature: 0.15, num_predict: 2800 },
        prompt: `You are a strict Russian grammar expert completing a worksheet. In each sentence the bracketed word is a noun in its DICTIONARY form that must be put into the correct case.
${RU_CASE_RULES}
${list}
Return ONLY JSON {"items":[{"case":"","answer":"","gender":"","number":"","english":"","why":"","endingHint":""}]} with EXACTLY ${read.length} items in the SAME order.
- "case": the case the sentence truly requires — nominative, accusative, genitive, dative, instrumental or prepositional.
- "answer": the bracketed noun correctly inflected into that case.
- "gender": "m", "f" or "n". "number": "singular" or "plural".
- "english": a natural English translation of the whole sentence.
- "why": ONE short English sentence naming the trigger.
- "endingHint": ONE short English hint for FORMING the ending — never the answer itself.`,
      })
      if (solved.length !== read.length) throw new Error('The grammar pass came back inconsistent — try importing again.')

      const raw = read.map((r, i) => ({
        baseNoun: r.baseNoun, sentenceTemplate: r.sentence,
        case: solved[i]?.case, answer: solved[i]?.answer,
        gender: solved[i]?.gender, number: solved[i]?.number,
        english: solved[i]?.english, why: solved[i]?.why, endingHint: solved[i]?.endingHint,
      }))
      if (raw.map(shapeCaseItem).filter(Boolean).length === 0) throw new Error('The exercises could not be turned into a drill — try again.')

      const set = {
        id: `imp-${Date.now()}`,
        title: `Imported · ${new Date().toLocaleDateString()}`,
        note: 'Read from a worksheet screenshot — flag anything that looks wrong with ⚑ and it will be skipped.',
        items: raw,
      }
      const next = [set, ...importedSets]
      setImportedSets(next); saveImported(next)
      setRuSetId(set.id); setRuSource('sets'); setImportFiles([]); setLoadingMsg('')
      startSet(set)
    } catch (e) {
      setLoadingMsg('')
      setErrorMsg((e.message || '').match(/Failed to fetch|Load failed/) ? 'Connection to the Mac Studio failed — check the tunnel / Ollama.' : (e.message || 'Import failed.'))
      setPhase('error')
    }
  }

  const start = () => {
    if (mode === 'draw') return startDraw()
    if (mode === 'alphabet') return setPhase('drill') // static board, no generation
    if (mode === 'verbs') return setPhase('drill') // TurkishVerbView generates itself
    if (mode === 'verbflow') return setPhase('drill') // VerbFlowView has its own setup + generation
    if (mode === 'sounds') return setPhase('drill') // KoreanSoundsView builds its list from the curated words
    if (mode === 'adjectives') return setPhase('drill') // GermanAdjectiveView builds locally
    if (mode === 'cases') {
      if (ruSource === 'import') return importFromImages()
      if (ruSource === 'sets') return startSet(allSets.find(s => s.id === ruSetId) || allSets[0])
    }
    return generate(false)
  }

  // Redo the whole set, or just the ones that needed help. "Whole set" restores
  // from baseItemsRef — otherwise a previous tricky-only retry would have
  // permanently shrunk `items`.
  const restartSet = () => {
    if (baseItemsRef.current.length) setItems(baseItemsRef.current)
    setIdx(0); setResults({}); setShowSummary(false); setCombo(0)
  }
  const retryTricky = () => {
    const tricky = items.filter((_, i) => {
      const r = results[i]
      return !r || !r.done || r.revealed || (r.caseAttempts || 0) > 0 || (r.endAttempts || 0) > 0
    })
    if (tricky.length === 0) return
    setItems(tricky); setIdx(0); setResults({}); setShowSummary(false); setCombo(0); setSetNum(n => n + 1)
  }

  // Reset per item.
  useEffect(() => {
    if (phase !== 'drill' || !item) return
    setStage('case'); setCaseChoice(null); setCaseResult(null); setCaseAttempts(0)
    setTyped(''); setEndAttempts(0); setEndRevealed(false); setEndRung(0)
    setExplainText(null); setExplainLoading(false); setShowExplain(false)
    setCaseHintShort(null); setCaseHintLong(null); setCaseHintLoading(false); setShowCaseHint(false); setHintMode('short')
    completedRef.current = false
  }, [idx, phase, item])

  useEffect(() => { if (stage === 'ending') setTimeout(() => inputRef.current?.focus(), 60) }, [stage])

  // Past the last item → the end-of-set report card, never a silent reset.
  const advance = useCallback(() => {
    clearTimeout(advanceTimerRef.current)
    const next = idxRef.current + 1
    if (next >= items.length) { setShowSummary(true); cueComplete(); return }
    setIdx(next)
  }, [items.length])

  const completeItem = useCallback(() => {
    if (completedRef.current) return
    completedRef.current = true
    setStage('done')
    cueCorrect()
    recordResult(idxRef.current, { done: true })
    const el = cardRef.current
    if (el) { const r = el.getBoundingClientRect(); celebrate(r.left + r.width / 2, r.top + r.height / 2, { count: 28, spread: 170 }) }
    setCombo(c => { const nc = c + 1; setBest(b => Math.max(b, nc)); if (nc % 5 === 0) celebrate(window.innerWidth / 2, window.innerHeight / 2.4, { count: 64, spread: 260 }); return nc })
    // Say the correct form aloud (just after the chime, so they don't collide) —
    // hearing the ending is half of how it gets remembered.
    if (soundOn && item?.answer) setTimeout(() => speak(item.answer, voices[lang] || voices['eng'], voiceSpeed), 380)
    // Long enough to read the finished sentence back; "Next →" skips ahead.
    advanceTimerRef.current = setTimeout(advance, 2600)
  }, [advance, recordResult, soundOn, item, lang, voiceSpeed])

  const pickCase = (key) => {
    if (!item || caseResult === 'right' || caseResult === 'revealed') return
    setCaseChoice(key)
    if (key === item.caseKey) {
      setCaseResult('right')
      cueCorrect()
      setTimeout(() => setStage('ending'), 750)
    } else {
      const n = caseAttempts + 1
      setCaseAttempts(n)
      setCaseResult('wrong')
      cueWrong()
      recordResult(idx, { caseAttempts: n })
      shake(caseRowRef.current)
      if (n >= 2) { setCaseChoice(item.caseKey); setCaseResult('revealed') }   // wait for the learner to tap Continue
    }
  }

  const isEndingCorrect = (val) => {
    const v = norm(val)
    return v === norm(item.answer) || norm((item.stem || '') + val) === norm(item.answer)
  }
  const onTyped = (val) => {
    setTyped(val)
    if (item && isEndingCorrect(val)) completeItem()
  }
  const checkEnding = () => {
    if (!item || completedRef.current) return
    if (isEndingCorrect(typed)) { completeItem(); return }
    const n = endAttempts + 1
    setEndAttempts(n)
    cueWrong()
    recordResult(idx, { endAttempts: n })
    shake(inputRef.current)
    if (n >= 5) setEndRevealed(true)
  }
  const skip = () => { setCombo(0); recordResult(idx, { skipped: true }); advance() }
  const flagWrong = () => { recordResult(idx, { flagged: true }); advance() } // skip a dubious item WITHOUT breaking the streak
  const hear = () => { if (item) speak(item.sentence, voices[lang] || voices['eng'], voiceSpeed) }
  const proceedToEnding = () => setStage('ending')

  // On-demand deeper explanation of why this case applies (3-4 lines + an example).
  const explainMore = useCallback(async () => {
    if (showExplain) { setShowExplain(false); return }
    setShowExplain(true)
    if (explainText || explainLoading || !item) return
    setExplainLoading(true)
    try {
      const caseLabel = CASE_BY_KEY[item.caseKey]?.label
      const p = `You are a friendly Russian tutor. In 3-4 very short lines, explain clearly WHY this sentence puts the noun in the ${caseLabel} case.
Sentence: ${item.sentence}
Noun: ${item.baseNoun} → ${item.answer} (${caseLabel})
Name the exact trigger (the preposition or the grammatical role). Then give ONE more short example sentence that uses the ${caseLabel} for the same reason. Plain English, no markdown, no preamble.`
      const res = await fetch(`${serverUrl}/api/generate`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey }, body: JSON.stringify({ model: 'gemma3:27b', prompt: p, stream: false, options: { temperature: 0.4, num_predict: 260 } }) })
      if (!res.ok) throw new Error()
      const data = await res.json()
      setExplainText((data.response || '').trim() || '—')
    } catch { setExplainText('Could not reach the Mac Studio for more detail.') }
    setExplainLoading(false)
  }, [showExplain, explainText, explainLoading, item, serverUrl, apiKey])

  // Strip any greeting/filler the model prepends despite instructions.
  const cleanHint = (s) => {
    let t = String(s || '').trim()
    t = t.replace(/^((ah|oh)[,!.\s]*)?(hello|hi|hey)\b[^.!?]*[.!?]\s*/i, '')
    t = t.replace(/^\s*(sure|okay|ok|alright|great|of course)\b[^.!?]*[.!?]\s*/i, '')
    t = t.replace(/^\s*let'?s\b[^.!?]*[.!?]\s*/i, '')
    return t.trim() || '—'
  }

  const fetchHint = useCallback(async (long) => {
    if (!item) return
    if (long && caseHintLong) { setHintMode('long'); return }
    if (!long && caseHintShort) { setHintMode('short'); return }
    setHintMode(long ? 'long' : 'short')
    setCaseHintLoading(true)
    const ctx = `The learner must choose the correct case for the bracketed noun. Point them at what to look at (the preposition right before the noun, the verb, or the noun's role: doer / receiver / possessed / tool "with" / location). Rules: NO greeting, no "let's"/"together"/"okay", do NOT name the case, do NOT give the inflected form.
Sentence: ${item.prefix} ⟦${item.baseNoun}⟧ ${item.suffix}
English: ${item.english}`
    const p = long
      ? `You are a concise Russian tutor. In 3-4 short sentences, guide the learner: what to notice, why it matters, and one plain-English analogy. ${ctx}`
      : `You are a concise Russian tutor. In 1-2 short sentences, straight to the point, tell the learner what to look at. ${ctx}`
    try {
      const res = await fetch(`${serverUrl}/api/generate`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey }, body: JSON.stringify({ model: 'gemma3:27b', prompt: p, stream: false, options: { temperature: 0.4, num_predict: long ? 240 : 90 } }) })
      if (!res.ok) throw new Error()
      const data = await res.json()
      const txt = cleanHint(data.response)
      if (long) setCaseHintLong(txt); else setCaseHintShort(txt)
    } catch {
      const err = 'Could not reach the Mac Studio for a hint.'
      if (long) setCaseHintLong(err); else setCaseHintShort(err)
    }
    setCaseHintLoading(false)
  }, [item, caseHintShort, caseHintLong, serverUrl, apiKey])

  // The 💡 button: open/close; on open, fetch the short hint by default.
  const caseHint = useCallback(() => {
    if (showCaseHint) { setShowCaseHint(false); return }
    setShowCaseHint(true); setHintMode('short')
    recordResult(idxRef.current, { hinted: true })
    if (!caseHintShort && !caseHintLoading) fetchHint(false)
  }, [showCaseHint, caseHintShort, caseHintLoading, fetchHint, recordResult])

  // ── setup ──────────────────────────────────────────────────────────────────
  if (phase !== 'drill') {
    return (
      <div className={`${styles.view} ${themeClass}`}>
        <ThemeBtn />
        <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>
        <div className={styles.panel}>
          <div className={styles.modeLabel}>Special Mode</div>
          <h1 className={styles.title}>Language-specific practice</h1>
          <p className={styles.sub}>Pick a language, then a mode built for its hardest bits.</p>

          {phase === 'loading' ? (
            <div className={styles.loading}><div className={styles.spinner} /><div className={styles.loadingText}>{loadingMsg || `Building ${count} exercises…`}</div></div>
          ) : (
            <>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Language</span>
                <select className={styles.select} value={lang} onChange={e => setLang(e.target.value)}>
                  {Object.keys(SPECIAL_MODES).map(code => <option key={code} value={code}>{languages[code] || code}</option>)}
                </select>
              </div>

              <div className={styles.field}>
                <span className={styles.fieldLabel}>Mode</span>
                <div className={styles.modeGrid}>
                  {modesForLang.map(m => (
                    <button key={m.id} disabled={!m.ready} className={`${styles.modeCard} ${mode === m.id ? styles.modeCardActive : ''} ${!m.ready ? styles.modeCardSoon : ''}`} onClick={() => m.ready && setMode(m.id)}>
                      <span className={styles.modeName}>{m.name}{!m.ready && ' · soon'}</span>
                      <span className={styles.modeDesc}>{m.desc}</span>
                    </button>
                  ))}
                  {modesForLang.length === 0 && <div className={styles.modeDesc}>No special modes for this language yet.</div>}
                </div>
              </div>

              {activeMode?.ready && mode === 'cases' && (
                <>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Where the exercises come from</span>
                    <div className={styles.chipRow}>
                      {[['sets', '📋 My homework'], ['import', '📸 Import a sheet'], ['ai', '✦ AI generated']].map(([v, l]) => (
                        <button key={v} className={`${styles.chip} ${ruSource === v ? styles.chipActive : ''}`} onClick={() => setRuSource(v)}>{l}</button>
                      ))}
                    </div>
                  </div>

                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Your Russian level — how much help the endings get</span>
                    <div className={styles.chipRow}>
                      {RU_LEVELS.map(l => (
                        <button key={l} className={`${styles.chip} ${effLevel === l ? styles.chipActive : ''}`} onClick={() => setRuLevel(l)}>{l}</button>
                      ))}
                    </div>
                    <div className={styles.teachTip}>
                      {rungsForLevel(effLevel) >= 4 ? 'Full scaffolding: the case question, an anchor word to copy, a shortlist of endings, then the rule.'
                        : rungsForLevel(effLevel) === 3 ? 'The case question, an anchor word to copy, then a shortlist of possible endings.'
                          : rungsForLevel(effLevel) === 2 ? 'The case question and an anchor word — you work the ending out from there.'
                            : 'Just the case question. At this level the endings should come from memory.'}
                      {!ruLevel && profileLevel ? ` (from your profile: ${profileLevel})` : ''}
                    </div>
                  </div>

                  {ruSource === 'sets' && (
                    <div className={styles.field}>
                      <span className={styles.fieldLabel}>Set</span>
                      <select className={styles.select} value={ruSetId} onChange={e => setRuSetId(e.target.value)}>
                        {allSets.map(s => <option key={s.id} value={s.id}>{s.title} · {(s.items || []).length} exercises</option>)}
                      </select>
                      <div className={styles.teachTip}>{allSets.find(s => s.id === ruSetId)?.note || ''}</div>
                    </div>
                  )}

                  {ruSource === 'import' && (
                    <div className={styles.field}>
                      <span className={styles.fieldLabel}>Screenshots of the worksheet</span>
                      <input type="file" multiple accept=".jpg,.jpeg,.png,.webp" className={styles.select} style={{ cursor: 'pointer' }}
                        onChange={e => setImportFiles(Array.from(e.target.files || []))} />
                      <div className={styles.teachBox}>
                        <div className={styles.teachLine}>Works on the usual sheet: a sentence with the noun in <strong>(brackets)</strong> and a blank to fill in.</div>
                        <div className={styles.teachTip}>The Mac Studio reads the sentences, then works out the case and the correct form. The set is saved on this device, so you can redo it any time.</div>
                      </div>
                      {importFiles.length > 0 && <div className={styles.teachTip}>{importFiles.length} image{importFiles.length === 1 ? '' : 's'} ready</div>}
                    </div>
                  )}

                  {ruSource === 'ai' && (
                    <>
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>Focus (optional)</span>
                        <textarea className={styles.queryBox} value={query} onChange={e => setQuery(e.target.value)} rows={2}
                          placeholder={'Leave blank for all six cases, or focus it —\ne.g. "genitive and dative", "prepositional case with places"'} />
                      </div>
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>How many</span>
                        <div className={styles.chipRow}>{[5, 10, 15].map(n => <button key={n} className={`${styles.chip} ${count === n ? styles.chipActive : ''}`} onClick={() => setCount(n)}>{n}</button>)}</div>
                      </div>
                    </>
                  )}
                </>
              )}

              {activeMode?.ready && mode === 'sounds' && (
                <>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Syllables per word</span>
                    <div className={styles.chipRow}>
                      {(lang === 'tur' ? [1, 2, 3, 4] : [1, 2, 3]).map(n => (
                        <button key={n} className={`${styles.chip} ${soundLens.includes(n) ? styles.chipActive : ''}`}
                          onClick={() => setSoundLens(l => (l.includes(n) ? (l.length > 1 ? l.filter(x => x !== n) : l) : [...l, n].sort()))}>{lang === 'tur' && n === 4 ? '4+' : n}</button>
                      ))}
                    </div>
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Your own words (optional)</span>
                    <textarea className={styles.queryBox} value={soundCustom} onChange={e => setSoundCustom(e.target.value)} rows={2}
                      placeholder={lang === 'tur'
                        ? 'Turkish words, comma separated — add a meaning with "=":\nkitap, öğretmen=teacher, İstanbul'
                        : 'Hangul words, comma separated — add a meaning with "=":\n비행기, 사과=apple, 지하철=subway'} />
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>How many</span>
                    <div className={styles.chipRow}>{[5, 10, 15].map(n => <button key={n} className={`${styles.chip} ${count === n ? styles.chipActive : ''}`} onClick={() => setCount(n)}>{n}</button>)}</div>
                  </div>
                </>
              )}

              {activeMode?.ready && mode === 'verbs' && (
                <>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Tense (one at a time)</span>
                    <div className={styles.chipRow}>
                      {TR_TENSES.map(t => (
                        <button key={t.key} className={`${styles.chip} ${trTense === t.key ? styles.chipActive : ''}`} onClick={() => setTrTense(t.key)}>{t.label}</button>
                      ))}
                    </div>
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Focus (optional)</span>
                    <textarea className={styles.queryBox} value={query} onChange={e => setQuery(e.target.value)} rows={2}
                      placeholder={'Leave blank for common verbs, or focus it —\ne.g. "gelmek, gitmek, yapmak", "everyday verbs"'} />
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>How many</span>
                    <div className={styles.chipRow}>{[5, 10, 15].map(n => <button key={n} className={`${styles.chip} ${count === n ? styles.chipActive : ''}`} onClick={() => setCount(n)}>{n}</button>)}</div>
                  </div>
                </>
              )}

              {activeMode?.ready && mode === 'adjectives' && (
                <>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>The three patterns</span>
                    <div className={styles.teachBox}>
                      {DEU_PATTERNS.map(p => (
                        <div key={p.key} className={styles.teachLine}>
                          <strong>{p.name}</strong> — {p.trigger}. <span className={styles.teachDim}>{p.why}</span>
                        </div>
                      ))}
                      <div className={styles.teachTip}>You choose the pattern, then the ending. Open the ▦ tables any time — they guide you to the exact cell.</div>
                    </div>
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Number</span>
                    <div className={styles.chipRow}>
                      {[['mixed', 'Mixed'], ['sg', 'Singular'], ['pl', 'Plural']].map(([v, l]) => (
                        <button key={v} className={`${styles.chip} ${deuNumber === v ? styles.chipActive : ''}`} onClick={() => setDeuNumber(v)}>{l}</button>
                      ))}
                    </div>
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Focus pattern</span>
                    <div className={styles.chipRow}>
                      {[['all', 'All three'], ['weak', 'Weak'], ['mixed', 'Mixed'], ['strong', 'Strong']].map(([v, l]) => (
                        <button key={v} className={`${styles.chip} ${deuPattern === v ? styles.chipActive : ''}`} onClick={() => setDeuPattern(v)}>{l}</button>
                      ))}
                    </div>
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>How many</span>
                    <div className={styles.chipRow}>{[5, 10, 15].map(n => <button key={n} className={`${styles.chip} ${count === n ? styles.chipActive : ''}`} onClick={() => setCount(n)}>{n}</button>)}</div>
                  </div>
                </>
              )}

              {activeMode?.ready && mode === 'draw' && (
                <>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Characters</span>
                    <div className={styles.chipRow}>
                      {[['1', 'Single'], ['2', 'Up to 2'], ['3', 'Up to 3']].map(([v, l]) => (
                        <button key={v} className={`${styles.chip} ${drawLen === v ? styles.chipActive : ''}`} onClick={() => setDrawLen(v)}>{l}</button>
                      ))}
                    </div>
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>How many words</span>
                    <div className={styles.chipRow}>{[5, 10, 15].map(n => <button key={n} className={`${styles.chip} ${count === n ? styles.chipActive : ''}`} onClick={() => setCount(n)}>{n}</button>)}</div>
                  </div>
                </>
              )}

              {phase === 'error' && <div className={styles.error}>{errorMsg}</div>}
              <button className={styles.generateBtn} onClick={start}
                disabled={!activeMode?.ready || (mode === 'cases' && ruSource === 'import' && importFiles.length === 0)}>
                {mode === 'cases' && ruSource === 'import' ? '📸 Read the sheet & start' : '✦ Start practice'}
              </button>
            </>
          )}
        </div>
      </div>
    )
  }

  // ── Korean alphabet: tap-to-hear board ──────────────────────────────────────
  if (mode === 'alphabet') {
    return <HangulBoard voiceSpeed={voiceSpeed} themeClass={themeClass} themeBtn={<ThemeBtn />} onClose={() => setPhase('setup')} />
  }

  // ── Korean handwriting: hand off to the shared board (hear → write) ─────────
  if (mode === 'draw') {
    return <HandwritingView cards={drawCards} autoSpeak checkDrawing voiceSpeed={voiceSpeed} onClose={() => setPhase('setup')} />
  }

  // ── "Sound it out": letters light up as you hear, then say, a word ────────
  if (mode === 'sounds') {
    const Drill = lang === 'tur' ? TurkishSoundsView : KoreanSoundsView
    return <Drill lengths={soundLens} count={count} custom={soundCustom} voiceSpeed={voiceSpeed} onBack={() => setPhase('setup')} onClose={onClose} />
  }

  // ── Spoken verb drill (any language): the AI-built flow, language pre-picked ─
  if (mode === 'verbflow') {
    return <VerbFlowView standalone lang={lang} voiceSpeed={voiceSpeed} onClose={() => setPhase('setup')} />
  }

  // ── Turkish verb endings: hand off to its own two-stage drill ───────────────
  if (mode === 'verbs') {
    return <TurkishVerbView tenseKey={trTense} count={count} query={query} voiceSpeed={voiceSpeed} onBack={() => setPhase('setup')} onClose={onClose} />
  }

  // ── German adjective endings: hand off to its own two-stage drill ───────────
  if (mode === 'adjectives') {
    return <GermanAdjectiveView count={count} patternFocus={deuPattern} numberMode={deuNumber} voiceSpeed={voiceSpeed} onBack={() => setPhase('setup')} onClose={onClose} />
  }

  // ── drill (Russian cases) ───────────────────────────────────────────────────
  const roman = romanizationOf(item?.sentence, lang)
  const caseObj = CASE_BY_KEY[item?.caseKey]
  const explainPanel = showExplain ? (
    <div className={styles.explainPanel}>{explainLoading ? <span className={styles.dots}>thinking…</span> : (explainText || '')}</div>
  ) : null
  const revealLetters = endRevealed ? (item?.ending || '').length : Math.max(0, Math.min((item?.ending || '').length, endAttempts - 1))
  const endingScaffold = (item?.stem || '') + (item?.ending || '').slice(0, revealLetters)
  const endingBlanks = '_'.repeat(Math.max(0, (item?.ending || '').length - revealLetters))

  return (
    <div className={`${styles.view} ${themeClass}`}>
      <ThemeBtn />
      <button className={styles.closeBtn} onClick={() => setPhase('setup')}><CloseIcon /></button>
      {regenerating && <div className={styles.regenOverlay}><div className={styles.spinner} /><div className={styles.loadingText}>New set…</div></div>}

      <div className={styles.drill}>
        <div className={styles.drillTop}>
          <span className={styles.modeLabel}>{setTitle || `Set ${setNum}`} · Cases · Russian</span>
          <div className={styles.comboWrap}><span className={styles.comboFire}>{combo >= 3 ? '🔥' : '✦'}</span><span className={styles.combo} ref={comboRef}>{combo}</span></div>
        </div>

        <div className={styles.card} key={idx} ref={cardRef}>
          <div className={styles.sentenceLine}>
            <span className={styles.ctx}>{item?.prefix}</span>
            <span className={styles.cue}>({item?.baseNoun})</span>
            {stage === 'done'
              ? <span className={styles.answerGot}>{item?.answer}</span>
              : <span className={styles.slot}>{caseObj && (caseResult === 'right' || caseResult === 'revealed' || stage !== 'case') ? caseObj.label.slice(0, 3) + '.' : '···'}</span>}
            <span className={styles.ctx}>{item?.suffix}</span>
          </div>
          {item?.english && <div className={styles.english}>{item.english}</div>}
          {roman && <div className={styles.roman}>{roman}</div>}

          {/* stage 1: pick the case */}
          {stage === 'case' && (
            <>
              <div className={styles.stageLabel}>Which case does <strong>{item?.baseNoun}</strong> take here?</div>
              <div className={styles.caseGrid} ref={caseRowRef}>
                {CASES.map(c => {
                  const chosen = caseChoice === c.key
                  const isAnswer = item?.caseKey === c.key
                  const cls = [
                    styles.caseBtn,
                    caseResult && isAnswer && (caseResult === 'right' || caseResult === 'revealed') ? styles.caseRight : '',
                    chosen && caseResult === 'wrong' && !isAnswer ? styles.caseWrong : '',
                  ].join(' ')
                  return (
                    <div key={c.key} className={styles.caseCell}>
                      <button className={cls} onClick={() => pickCase(c.key)}>
                        <span className={styles.caseName}>{c.label}</span>
                        <span className={styles.caseRu}>{c.ru}</span>
                        <span className={styles.caseQ}>{c.q}</span>
                      </button>
                      <div className={styles.caseInfo}>
                        <span className={styles.caseInfoHead}>{c.label} · {c.ru}</span>
                        <span className={styles.caseInfoQ}>{c.qEn} · {c.q}</span>
                        {c.lines.map((l, i) => <span key={i} className={styles.caseInfoLine}>{l}</span>)}
                        {c.ex.map((e, i) => <span key={`e${i}`} className={styles.caseInfoEx}><b>{e.ru}</b> — {e.en}</span>)}
                      </div>
                    </div>
                  )
                })}
              </div>
              <button className={styles.hintBtn} onClick={caseHint}>{showCaseHint ? 'hide hint' : '💡 What should I notice?'}</button>
              {showCaseHint && (
                <div className={styles.hintPanel}>
                  {caseHintLoading
                    ? <span className={styles.dots}>thinking…</span>
                    : (hintMode === 'long' ? caseHintLong : caseHintShort) || ''}
                  {!caseHintLoading && (
                    <div className={styles.hintMoreRow}>
                      {hintMode === 'short'
                        ? <button className={styles.linkBtn} onClick={() => fetchHint(true)}>more detail →</button>
                        : <button className={styles.linkBtn} onClick={() => setHintMode('short')}>← shorter</button>}
                    </div>
                  )}
                </div>
              )}

              {caseResult === 'wrong' && caseAttempts < 2 && (
                <div className={styles.nudge}>Not that one — check the hint above, then try again.</div>
              )}
              {caseResult === 'revealed' && (
                <>
                  <div className={styles.whyBox}>
                    It's <strong>{caseObj?.label}</strong> ({caseObj?.ru}). {item?.why}
                    {' '}<button className={styles.explainLink} onClick={explainMore}>{showExplain ? 'less' : 'explain more'}</button>
                  </div>
                  {explainPanel}
                  <button className={styles.continueBtn} onClick={proceedToEnding}>Continue →</button>
                </>
              )}
            </>
          )}

          {/* stage 2: form the ending */}
          {stage === 'ending' && (
            <>
              <div className={styles.whyBox}>
                <strong>{caseObj?.label}</strong> ✓ — {item?.why}
                {' '}<button className={styles.explainLink} onClick={explainMore}>{showExplain ? 'less' : 'explain more'}</button>
              </div>
              {explainPanel}
              <div className={styles.stageLabel}>Now write <strong>{item?.baseNoun}</strong> in the {caseObj?.label}:</div>
              <div className={styles.endingScaffold}>
                <span className={styles.stem}>{endingScaffold}</span><span className={styles.blankInline}>{endingBlanks}</span>
              </div>
              <input
                ref={inputRef}
                className={styles.answerInput}
                value={typed}
                onChange={e => onTyped(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') checkEnding() }}
                placeholder="type the correct form…"
                spellCheck="false"
                autoComplete="off"
              />
              <div className={styles.endActions}>
                <button className={styles.checkBtn} onClick={checkEnding}>Check</button>
                {endAttempts >= 1 && (
                  <span className={styles.endHint}>💡 {item?.ending
                    ? `the ending changes — ${item.ending.length} letter${item.ending.length === 1 ? '' : 's'} (shown greyed above)`
                    : 'no change — same as the dictionary form'}</span>
                )}
                {!endRevealed && <button className={styles.linkBtn} onClick={() => { setEndRevealed(true); recordResult(idx, { revealed: true }) }}>reveal</button>}
              </div>

              {/* Guided narrowing: the question → an anchor word you already
                  know → the shortlist → the rule. Never the answer, and how far
                  it goes depends on the learner's level. */}
              {maxRungs > 0 && (
                <button className={styles.hintBtn} disabled={endRung >= maxRungs}
                  onClick={() => { setEndRung(r => Math.min(maxRungs, r + 1)); cueTick(); recordResult(idx, { hinted: true }) }}>
                  {endRung === 0 ? '💡 Help me think it through' : endRung < maxRungs ? '💡 One more nudge →' : '💡 That’s all the help'}
                </button>
              )}
              {endRung > 0 && (
                <div className={styles.ladder}>
                  {endLadder.slice(0, endRung).map(r => (
                    <div key={r.key} className={styles.rung}>
                      <div className={styles.rungHead}>
                        <span>{r.heading}</span>
                        {r.speak && (
                          <button className={styles.rungSpk} title="Hear it" onClick={() => speak(r.speak, voices[lang] || voices['eng'], voiceSpeed)}>
                            <VolumeUpIcon fontSize="inherit" />
                          </button>
                        )}
                      </div>
                      {r.lines.map((l, i) => <div key={i} className={styles.rungLine}>{l}</div>)}
                      {r.chips && r.chips.length > 0 && (
                        <div className={styles.endChips}>{r.chips.map(c => <span key={c} className={styles.endChip}>-{c}</span>)}</div>
                      )}
                    </div>
                  ))}
                  {endRung >= maxRungs && <div className={styles.rungCap}>{capNoteForLevel(effLevel)}</div>}
                </div>
              )}

              {endRevealed && <div className={styles.revealNote}>Answer: <strong>{item?.answer}</strong></div>}
            </>
          )}

          {stage === 'done' && (
            <>
              <div className={styles.doneMsg}>{caseAttempts === 0 && endAttempts === 0 && endRung === 0 ? '★ Perfect — straight from memory' : '✓ Nice'}</div>
              <div className={styles.endActions}>
                <button className={styles.linkBtn} onClick={hear}>🔊 hear the whole sentence</button>
                <button className={styles.checkBtn} onClick={advance}>Next →</button>
              </div>
            </>
          )}
        </div>

        <div className={styles.controls}>
          <button className={styles.sideBtn} onClick={hear} title="Hear the full sentence"><VolumeUpIcon /></button>
          <button className={styles.sideBtn} onClick={() => setShowRef(true)} title="Endings reference board — guides you to the right cell"><TableChartIcon /></button>
          <button className={styles.sideBtn} onClick={skip} title="Skip (resets streak)"><SkipNextIcon /></button>
          <button className={styles.sideBtn} onClick={() => { const on = !soundOn; setSoundOn(on); setMuted(!on) }} title={soundOn ? 'Mute the sounds' : 'Turn the sounds on'}>
            {soundOn ? <MusicNoteIcon /> : <MusicOffIcon />}
          </button>
        </div>
        <div className={styles.footRow}>
          <span className={styles.progress}>{idx + 1} / {items.length} · best {best}</span>
          <button className={styles.linkBtn} onClick={flagWrong} title="Skip a dubious exercise — keeps your streak">⚑ looks wrong</button>
        </div>
      </div>
      {showRef && <RefBoard item={item} onClose={() => setShowRef(false)} />}
      {showSummary && (
        <SetSummary items={items} results={results} title={setTitle} aiMode={ruSource === 'ai'}
          username={username} langCode={lang} drillMode={mode}
          onRetry={retryTricky} onRestart={restartSet}
          onNewAi={() => { setShowSummary(false); generate(true) }}
          onClose={() => { setShowSummary(false); setPhase('setup') }} />
      )}
    </div>
  )
}

// End-of-set report card. Immediate per-question feedback keeps the correction
// close to the mistake; this is the other half — closure, a score to beat, and
// a shortlist of exactly what to redo.
function SetSummary({ items, results, title, aiMode, username, langCode, drillMode, onRetry, onRestart, onNewAi, onClose }) {
  const scored = items.map((it, i) => {
    const r = results[i] || {}
    const perfect = !!r.done && !r.revealed && !(r.caseAttempts > 0) && !(r.endAttempts > 0)
    return { it, r, perfect }
  })
  const total = scored.length
  const perfect = scored.filter(s => s.perfect).length
  const tricky = scored.filter(s => !s.perfect)
  const pct = Math.round((perfect / Math.max(1, total)) * 100)
  const grade = pct === 100 ? '🎉 Every one first time' : pct >= 80 ? 'Strong work' : pct >= 50 ? 'Getting there' : 'Worth another pass'
  const caseSlips = scored.filter(s => (s.r.caseAttempts || 0) > 0).length
  const endSlips = scored.filter(s => (s.r.endAttempts || 0) > 0).length

  return (
    <div className={styles.refOverlay} onClick={onClose}>
      <div className={styles.refModal} onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>
        <div className={styles.modeLabel}>{title || 'Set complete'}</div>
        <h2 className={styles.sumGrade}>{grade}</h2>

        <div className={styles.sumStats}>
          <div className={styles.sumStat}><span className={styles.sumNum}>{perfect}/{total}</span><span className={styles.sumLabel}>first time</span></div>
          <div className={styles.sumStat}><span className={styles.sumNum}>{pct}%</span><span className={styles.sumLabel}>clean</span></div>
          <div className={styles.sumStat}><span className={styles.sumNum}>{caseSlips}</span><span className={styles.sumLabel}>case slips</span></div>
          <div className={styles.sumStat}><span className={styles.sumNum}>{endSlips}</span><span className={styles.sumLabel}>ending slips</span></div>
        </div>

        {tricky.length > 0 ? (
          <>
            <div className={styles.sumHead}>Worth another look</div>
            <div className={styles.sumList}>
              {tricky.map((s, i) => (
                <div key={i} className={styles.sumRow}>
                  <span className={styles.sumMark}>{s.r.skipped ? '↷' : s.r.flagged ? '⚑' : s.r.revealed ? '👁' : '↻'}</span>
                  <span className={styles.sumSent}>
                    <span>{s.it.prefix} <b className={styles.sumAns}>{s.it.answer}</b> {s.it.suffix}</span>
                    <span className={styles.sumWhy}>{CASE_BY_KEY[s.it.caseKey]?.label} — {s.it.why}</span>
                  </span>
                </div>
              ))}
            </div>
          </>
        ) : <div className={styles.sumClean}>Nothing to review — clean sweep.</div>}

        <ShareResult username={username} title={title} langCode={langCode} drillMode={drillMode}
          total={total} perfect={perfect} scored={scored} />

        <div className={styles.refFoot}>
          {tricky.length > 0 && <button className={styles.checkBtn} onClick={onRetry}>Redo the {tricky.length} tricky {tricky.length === 1 ? 'one' : 'ones'} →</button>}
          <button className={styles.linkBtn} onClick={onRestart}>redo the whole set</button>
          {aiMode && <button className={styles.linkBtn} onClick={onNewAi}>new AI set</button>}
          <button className={styles.linkBtn} onClick={onClose}>done</button>
        </div>
      </div>
    </div>
  )
}

// Guided endings reference: shows the full table, then after 2s highlights the
// learner's CASE, then after 2s more spotlights the exact gender/number cell and
// reveals the answer. "Reveal now" jumps straight to the spotlight.
function RefBoard({ item, onClose }) {
  const [phase, setPhase] = useState(0) // 0 all · 1 case highlighted · 2 cell + answer
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 2000)
    const t2 = setTimeout(() => setPhase(2), 4000)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [])
  const activeCase = item?.caseKey
  const matchSub = (subs) => item?.number === 'pl'
    ? subs.find(s => s.num === 'pl')
    : (subs.find(s => s.g && s.g.includes(item?.gender)) || subs[0])

  return (
    <div className={styles.refOverlay} onClick={onClose}>
      <div className={styles.refModal} onClick={e => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>
        <div className={styles.modeLabel}>Endings reference</div>
        <div className={styles.refStatus}>
          {phase === 0 ? 'Find your case in the table…'
            : phase === 1 ? <>Your case: <strong>{CASE_BY_KEY[activeCase]?.label}</strong> — now the ending…</>
            : <>{item?.baseNoun} → <strong className={styles.refHi}>{item?.answer}</strong> ({CASE_BY_KEY[activeCase]?.label}, {item?.number === 'pl' ? 'plural' : ({ m: 'masculine', f: 'feminine', n: 'neuter' }[item?.gender] || item?.gender)})</>}
        </div>

        <div className={styles.refTable}>
          <div className={`${styles.refRow} ${styles.refHeadRow}`}>
            <div>g/n</div><div>Adjective</div><div>Noun</div><div>Examples</div>
          </div>
          {CASES.map(c => {
            const subs = DECL[c.key] || []
            const isActive = c.key === activeCase
            const target = isActive ? matchSub(subs) : null
            return (
              <div key={c.key} className={`${styles.refSection} ${phase >= 1 && isActive ? styles.refSectionActive : ''} ${phase >= 1 && !isActive ? styles.refDim : ''}`}>
                <div className={styles.refCaseLabel}>{c.label} · {c.ru}</div>
                {subs.map((s, i) => {
                  const isTarget = phase >= 2 && isActive && s === target
                  return (
                    <div key={i} className={`${styles.refRow} ${isTarget ? styles.refTarget : ''}`}>
                      <div className={styles.refGN}>{s.num === 'pl' ? 'pl.' : (s.g || []).join('/') + '.'}</div>
                      <div>{s.adj}</div>
                      <div className={styles.refNoun}>{s.noun}</div>
                      <div className={styles.refEx}>{s.ex}</div>
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>

        <div className={styles.refFoot}>
          {phase < 2
            ? <button className={styles.checkBtn} onClick={() => setPhase(2)}>Reveal my ending →</button>
            : <button className={styles.checkBtn} onClick={onClose}>Got it</button>}
        </div>
      </div>
    </div>
  )
}

// Send the finished set to a teacher. Teachers who have already assigned you
// homework are offered as one tap; anyone else can be typed in.
function ShareResult({ username, title, langCode, drillMode, total, perfect, scored }) {
  const { data } = useQuery(MY_TEACHERS_QUERY, { variables: { username }, skip: !username, errorPolicy: 'ignore' })
  const [shareResult, { loading }] = useMutation(SHARE_DRILL_RESULT_MUTATION)
  const teachers = data?.myTeachers || []
  const [to, setTo] = useState('')
  const [msg, setMsg] = useState('')
  useEffect(() => { setTo(t => (t || teachers[0] || '')) }, [teachers])
  if (!username) return null

  const send = async () => {
    setMsg('')
    try {
      const details = JSON.stringify(scored.map(s => ({
        sentence: `${s.it.prefix} ${s.it.answer} ${s.it.suffix}`.replace(/\s+/g, ' ').trim(),
        noun: s.it.baseNoun, answer: s.it.answer, case: s.it.caseKey,
        caseAttempts: s.r.caseAttempts || 0, endAttempts: s.r.endAttempts || 0,
        revealed: !!s.r.revealed, hinted: !!s.r.hinted, skipped: !!s.r.skipped, perfect: s.perfect,
      })))
      await shareResult({ variables: { username, teacher: to.trim(), input: { title: title || 'Practice set', language: langCode, mode: drillMode, total, perfect, details } } })
      setMsg(`Sent to ${to.trim()} ✓`)
    } catch (e) { setMsg(e.message || 'Could not share.') }
  }

  return (
    <div className={styles.shareBox}>
      <div className={styles.sumHead}>Share with your teacher</div>
      {teachers.length > 0 && (
        <div className={styles.chipRow}>
          {teachers.map(t => (
            <button key={t} className={`${styles.chip} ${to === t ? styles.chipActive : ''}`} onClick={() => setTo(t)}>{t}</button>
          ))}
        </div>
      )}
      <div className={styles.shareRow}>
        <input className={styles.select} style={{ flex: 1 }} value={to} onChange={e => setTo(e.target.value)}
          placeholder={teachers.length ? 'or another username…' : "your teacher's username"} spellCheck="false" autoComplete="off" />
        <button className={styles.checkBtn} onClick={send} disabled={loading || !to.trim()}>{loading ? 'Sending…' : 'Share →'}</button>
      </div>
      <div className={styles.sumWhy}>They see your score and which sentences needed help — not the whole session.</div>
      {msg && <div className={msg.endsWith('✓') ? styles.shareOk : styles.error}>{msg}</div>}
    </div>
  )
}

// Tap-to-hear Hangul board — click any letter to hear its sound (played as a
// syllable) and see its romanization. Fully self-contained: no model, no network.
function HangulBoard({ voiceSpeed, themeClass = '', themeBtn = null, onClose }) {
  const [last, setLast] = useState(null)
  // board: tap to hear · quiz: test me · words: name one letter's sound in a word
  const [tab, setTab] = useState('board')
  // What this learner gets right and wrong, from the practice tabs. The board
  // colours each letter by it, and can show only the ones that need work.
  const { stats, weak, answered, levelOf } = useLetterStats('kor')
  const [onlyWeak, setOnlyWeak] = useState(false)
  const LEVEL_COLOR = { weak: '#f59e0b', shaky: 'var(--amber, #fbbf24)', solid: 'var(--accent, #6ee7b7)', new: 'var(--muted-2, #8a8a95)' }
  // Tile look by level: untested slightly greyed, mastered as normal, shaky
  // with a quiet amber edge, weak with a warm pulsing glow (see .abcTile*).
  const LEVEL_CLASS = { new: styles.abcTileNew, shaky: styles.abcTileShaky, weak: styles.abcTileWeak, solid: '' }
  const say = (jamo, syllable, e) => {
    if (e) popScale(e.currentTarget)
    setLast(jamo)
    speak(syllable, voices.kor || voices['eng'], voiceSpeed)
  }
  const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(76px, 1fr))', gap: '0.55rem' }
  const heading = { fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.09em', color: 'var(--muted-2, #8a8a95)', margin: '0 0 0.6rem' }
  const section = (title, sub, allItems) => {
    const items = onlyWeak ? allItems.filter(([j]) => weak.includes(j)) : allItems
    if (!items.length) return null
    return (
      <div style={{ marginBottom: '1.7rem' }} key={title}>
        <div style={heading}>{title} <span style={{ color: 'var(--muted, #555560)' }}>· {sub}</span></div>
        <div style={grid}>
          {items.map(([j, r, syl]) => {
            const s = stats[j]
            const lvl = levelOf(s)
            return (
              <button key={j} onClick={(e) => say(j, syl, e)}
                title={s ? `${s.right} right · ${s.wrong} wrong` : 'not tested yet'}
                className={`${styles.abcTile} ${last === j ? styles.abcTileLast : ''} ${LEVEL_CLASS[lvl] || ''}`}>
                <span style={{ fontSize: '2rem', fontWeight: 600, lineHeight: 1 }}>{j}</span>
                <span style={{ fontSize: '0.78rem', color: 'var(--accent, #6ee7b7)', fontWeight: 500 }}>{r}</span>
                <span style={{ fontSize: '0.72rem', color: 'var(--muted-2, #8a8a95)' }}>{syl}</span>
                {s ? <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', color: LEVEL_COLOR[lvl], marginTop: '0.15rem' }}>{s.right}✓ {s.wrong}✗</span> : null}
              </button>
            )
          })}
        </div>
      </div>
    )
  }
  return (
    <div className={`${styles.view} ${themeClass}`}>
      {themeBtn}
      <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>
      <div style={{ maxWidth: 920, width: '100%', margin: '0 auto', padding: '2rem 1.5rem 3rem', height: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
        <div className={styles.modeLabel}>Korean · Alphabet</div>
        <h1 className={styles.title} style={{ marginBottom: '0.25rem' }}>
          {tab === 'board' ? '한글 — tap to hear' : tab === 'quiz' ? '한글 — test me' : '한글 — in words'}
        </h1>
        <p className={styles.sub} style={{ marginBottom: '1rem' }}>
          {tab === 'board'
            ? 'Tap any letter to hear its sound. Consonants are played with ㅏ (a); vowels with the silent ㅇ.'
            : tab === 'quiz'
              ? 'See a letter and pick its sound, or hear a sound and pick the letter. The wrong answers are its look-alikes on purpose.'
              : 'A real word, every letter\'s sound shown except one. Read it and name the highlighted letter\'s sound.'}
        </p>
        <div className={styles.chipRow} style={{ marginBottom: '1.5rem' }}>
          {[['board', 'Board'], ['quiz', 'Test me'], ['words', 'In words']].map(([k, name]) => (
            <button key={k} className={`${styles.chip} ${tab === k ? styles.chipActive : ''}`} onClick={() => setTab(k)}>{name}</button>
          ))}
        </div>
        {tab === 'quiz' && <HangulQuiz voiceSpeed={voiceSpeed} />}
        {tab === 'words' && <HangulWordCloze voiceSpeed={voiceSpeed} />}
        {tab === 'board' && (
          <>
            {/* What the practice tabs have learned about this learner. */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.6rem 1rem', marginBottom: '1.2rem', fontSize: '0.85rem', color: 'var(--muted-2, #8a8a95)' }}>
              {answered === 0
                ? <span>Nothing tested yet — try <b>Test me</b> or <b>In words</b>, and the letters you get wrong will show here in red.</span>
                : weak.length
                  ? <span>{answered} answers so far · needs work: <b style={{ color: 'var(--danger, #f87171)', fontSize: '1rem' }}>{weak.join(' ')}</b></span>
                  : <span>{answered} answers so far · nothing stands out as weak right now.</span>}
              {weak.length ? (
                <button className={`${styles.chip} ${onlyWeak ? styles.chipActive : ''}`} onClick={() => setOnlyWeak(w => !w)}>
                  {onlyWeak ? 'Show all letters' : 'Show only what I get wrong'}
                </button>
              ) : null}
              {weak.length ? <button className={styles.chip} onClick={() => setTab('quiz')}>Test these</button> : null}
            </div>
            {section('Consonants', '자음', HANGUL.consonants)}
            {section('Double consonants', '쌍자음', HANGUL.doubles)}
            {section('Vowels', '모음', HANGUL.vowels)}
            {section('Complex vowels', '복합 모음', HANGUL.complexVowels)}
          </>
        )}
      </div>
    </div>
  )
}
